'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import Icon from '@/components/ui/AppIcon';
import Breadcrumb from '@/components/Breadcrumb';

const supabase = createClient();

const STATI = ['aperto','preso_in_carico','in_lavorazione','in_attesa_ricambi','in_attesa_utente','sospeso','risolto','chiuso'];
const PRIORITA = ['critico','urgente','alta','normale','bassa','pianificata'];
const TIPI = ['guasto_hardware','problema_software','configurazione','richiesta','change'];

const prioritaColor: Record<string, string> = {
  critico: 'bg-red-500/20 text-red-400 border-red-500/30',
  urgente: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  alta: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  normale: 'bg-sky-500/20 text-sky-400 border-sky-500/30',
  bassa: 'bg-green-500/20 text-green-400 border-green-500/30',
  pianificata: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
};

const statoColor: Record<string, string> = {
  aperto: 'bg-red-500/20 text-red-400',
  preso_in_carico: 'bg-orange-500/20 text-orange-400',
  in_lavorazione: 'bg-amber-500/20 text-amber-400',
  in_attesa_ricambi: 'bg-purple-500/20 text-purple-400',
  in_attesa_utente: 'bg-blue-500/20 text-blue-400',
  sospeso: 'bg-gray-500/20 text-gray-400',
  risolto: 'bg-green-500/20 text-green-400',
  chiuso: 'bg-slate-500/20 text-slate-400',
};

const slaColor: Record<string, string> = {
  rispettato: 'text-green-400',
  a_rischio: 'text-amber-400',
  violato: 'text-red-400',
};

interface Ticket {
  id: string;
  numero_ticket: string;
  titolo: string;
  descrizione: string;
  tipo: string;
  stato: string;
  priorita: string;
  reparto: string;
  aperto_da_nome: string;
  aperto_da_email: string;
  assegnato_a_nome: string;
  sla_stato: string;
  data_presa_in_carico: string | null;
  data_risoluzione: string | null;
  ore_lavoro: number;
  valutazione: number | null;
  created_at: string;
}

interface Commento {
  id: string;
  autore_nome: string;
  testo: string;
  visibile_utente: boolean;
  created_at: string;
}

// Generate a collision-resistant ticket number using timestamp + random suffix
function generateTicketNumber(prefix: string): string {
  const now = Date.now();
  const rand = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
  return `${prefix}-${now}-${rand}`;
}

export default function TicketContent() {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filtroStato, setFiltroStato] = useState('');
  const [filtroPriorita, setFiltroPriorita] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [search, setSearch] = useState('');
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [commenti, setCommenti] = useState<Commento[]>([]);
  const [nuovoCommento, setNuovoCommento] = useState('');
  const [commentoVisibile, setCommentoVisibile] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ titolo: '', descrizione: '', tipo: 'richiesta', priorita: 'normale', reparto: '', aperto_da_nome: '', aperto_da_email: '', assegnato_a_nome: '' });
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState({ aperti: 0, in_lavorazione: 0, risolti: 0, sla_violati: 0 });
  const { profile } = useAuth();
  const [evaluatingSla, setEvaluatingSla] = useState(false);

  const exportCSV = () => {
    if (tickets.length === 0) return;
    const headers = ['Numero', 'Titolo', 'Tipo', 'Stato', 'Priorità', 'Reparto', 'Aperto da', 'Assegnato a', 'SLA', 'Creato', 'Risolto'];
    const rows = tickets.map(t => [
      t.numero_ticket || '', t.titolo || '', t.tipo || '', t.stato || '',
      t.priorita || '', t.reparto || '', t.aperto_da_nome || '', t.assegnato_a_nome || '',
      t.sla_stato || '',
      t.created_at ? new Date(t.created_at).toLocaleDateString('it-IT') : '',
      t.data_risoluzione ? new Date(t.data_risoluzione).toLocaleDateString('it-IT') : '',
    ]);
    const csv = '\uFEFF' + [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tickets_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      let q = supabase.from('tickets').select('*').order('created_at', { ascending: false });
      if (filtroStato) q = q.eq('stato', filtroStato);
      if (filtroPriorita) q = q.eq('priorita', filtroPriorita);
      if (filtroTipo) q = q.eq('tipo', filtroTipo);
      if (search) q = q.or(`titolo.ilike.%${search}%,numero_ticket.ilike.%${search}%,reparto.ilike.%${search}%`);
      const { data, error: e } = await q;
      if (e) throw e;
      setTickets(data || []);
      const d = data || [];
      setStats({
        aperti: d.filter(t => t.stato === 'aperto').length,
        in_lavorazione: d.filter(t => t.stato === 'in_lavorazione').length,
        risolti: d.filter(t => t.stato === 'risolto' || t.stato === 'chiuso').length,
        sla_violati: d.filter(t => t.sla_stato === 'violato').length,
      });
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [filtroStato, filtroPriorita, filtroTipo, search]);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  const fetchCommenti = async (ticketId: string) => {
    const { data } = await supabase.from('ticket_commenti').select('*').eq('ticket_id', ticketId).order('created_at');
    setCommenti(data || []);
  };

  const openTicket = (t: Ticket) => {
    setSelectedTicket(t);
    fetchCommenti(t.id);
  };

  const aggiungiCommento = async () => {
    if (!nuovoCommento.trim() || !selectedTicket) return;
    setSaving(true);
    // Use real user name from profile instead of hardcoded 'Tecnico IT'
    const autoreNome = profile?.full_name || profile?.email || 'Utente';
    const { error: e } = await supabase.from('ticket_commenti').insert({
      ticket_id: selectedTicket.id,
      autore_nome: autoreNome,
      testo: nuovoCommento,
      visibile_utente: commentoVisibile,
    });
    if (!e) {
      setNuovoCommento('');
      fetchCommenti(selectedTicket.id);
    }
    setSaving(false);
  };

  const cambiaStato = async (ticketId: string, nuovoStato: string) => {
    const updates: Record<string, any> = { stato: nuovoStato };
    if (nuovoStato === 'preso_in_carico') updates.data_presa_in_carico = new Date().toISOString();
    if (nuovoStato === 'risolto') updates.data_risoluzione = new Date().toISOString();
    await supabase.from('tickets').update(updates).eq('id', ticketId);
    fetchTickets();
    if (selectedTicket?.id === ticketId) setSelectedTicket(prev => prev ? { ...prev, stato: nuovoStato } : null);
  };

  const creaTicket = async () => {
    if (!form.titolo.trim()) { toast.error('Titolo obbligatorio'); return; }
    if (!form.descrizione.trim()) { toast.error('Descrizione obbligatoria'); return; }
    setSaving(true);
    // Use sequential human-readable ticket number via DB RPC, fallback to timestamp
    let numero: string;
    try {
      const { data: numData } = await supabase.rpc('generate_ticket_number');
      numero = numData || `TK-${Date.now()}`;
    } catch {
      numero = `TK-${Date.now()}-${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`;
    }
    const { error: e } = await supabase.from('tickets').insert({ ...form, numero_ticket: numero, sla_stato: 'rispettato' });
    if (!e) {
      setShowForm(false);
      setForm({ titolo: '', descrizione: '', tipo: 'richiesta', priorita: 'normale', reparto: '', aperto_da_nome: '', aperto_da_email: '' });
      fetchTickets();
    }
    setSaving(false);
  };

  const evaluateSla = async () => {
    setEvaluatingSla(true);
    try {
      await supabase.rpc('evaluate_sla_tickets');
      await fetchTickets();
    } catch (e: any) {
      console.error('SLA evaluation error:', e.message);
    } finally {
      setEvaluatingSla(false);
    }
  };

  return (
    <div className="flex h-full gap-4">
      {/* Left Panel */}
      <div className={`flex flex-col ${selectedTicket ? 'w-1/2' : 'w-full'} transition-all`}>
        {/* Breadcrumb */}
        <Breadcrumb items={[{ label: 'Gestione Ticket' }]} />
        {/* Stats */}
        <div className="grid grid-cols-4 gap-3 mb-4">
          {[
            { label: 'Aperti', value: stats.aperti, color: 'text-red-400', icon: 'ExclamationCircleIcon' },
            { label: 'In Lavorazione', value: stats.in_lavorazione, color: 'text-amber-400', icon: 'WrenchScrewdriverIcon' },
            { label: 'Risolti', value: stats.risolti, color: 'text-green-400', icon: 'CheckCircleIcon' },
            { label: 'SLA Violati', value: stats.sla_violati, color: 'text-red-500', icon: 'ClockIcon' },
          ].map(s => (
            <div key={s.label} className="bg-surface-elevated rounded-xl p-3 border border-border">
              <div className="flex items-center gap-2 mb-1">
                <Icon name={s.icon as any} size={16} className={s.color} />
                <span className="text-xs text-muted-color">{s.label}</span>
              </div>
              <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-3">
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Cerca ticket..." className="flex-1 min-w-[160px] bg-surface-elevated border border-border rounded-lg px-3 py-2 text-sm text-white placeholder-muted-color" />
          <select value={filtroStato} onChange={e => setFiltroStato(e.target.value)} className="bg-surface-elevated border border-border rounded-lg px-3 py-2 text-sm text-white">
            <option value="">Tutti gli stati</option>
            {STATI.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
          </select>
          <select value={filtroPriorita} onChange={e => setFiltroPriorita(e.target.value)} className="bg-surface-elevated border border-border rounded-lg px-3 py-2 text-sm text-white">
            <option value="">Tutte le priorità</option>
            {PRIORITA.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <select value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)} className="bg-surface-elevated border border-border rounded-lg px-3 py-2 text-sm text-white">
            <option value="">Tutti i tipi</option>
            {TIPI.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
          </select>
          <button onClick={() => setShowForm(true)} className="flex items-center gap-2 bg-sky-600 hover:bg-sky-500 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors">
            <Icon name="PlusIcon" size={16} /> Nuovo Ticket
          </button>
          <button onClick={evaluateSla} disabled={evaluatingSla} className="flex items-center gap-2 bg-amber-600/20 hover:bg-amber-600/30 text-amber-400 border border-amber-500/30 px-4 py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50" title="Rivaluta stato SLA per tutti i ticket aperti">
            <Icon name={evaluatingSla ? 'ArrowPathIcon' : 'ClockIcon'} size={16} className={evaluatingSla ? 'animate-spin' : ''} />
            {evaluatingSla ? 'Valutazione...' : 'Rivaluta SLA'}
          </button>
          <button onClick={exportCSV} disabled={tickets.length === 0} className="flex items-center gap-2 bg-surface-elevated border border-border hover:border-sky-500/40 text-secondary-color hover:text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50">
            <Icon name="ArrowDownTrayIcon" size={16} />
            Esporta CSV
          </button>
        </div>

        {/* Ticket List */}
        {error && <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-400 text-sm mb-3">{error}</div>}
        {loading ? (
          <div className="flex items-center justify-center py-12"><div className="w-8 h-8 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" /></div>
        ) : (
          <div className="flex-1 overflow-y-auto space-y-2">
            {tickets.length === 0 ? (
              <div className="text-center py-12 text-muted-color">Nessun ticket trovato</div>
            ) : tickets.map(t => (
              <div key={t.id} onClick={() => openTicket(t)} className={`bg-surface-elevated border rounded-xl p-4 cursor-pointer hover:border-sky-500/50 transition-all ${selectedTicket?.id === t.id ? 'border-sky-500/50' : 'border-border'}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-mono text-muted-color">{t.numero_ticket}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${prioritaColor[t.priorita] || ''}`}>{t.priorita}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${statoColor[t.stato] || ''}`}>{t.stato.replace(/_/g, ' ')}</span>
                    {t.sla_stato !== 'rispettato' && <span className={`text-xs font-semibold ${slaColor[t.sla_stato]}`}>SLA {t.sla_stato.replace(/_/g, ' ')}</span>}
                  </div>
                  <span className="text-xs text-muted-color whitespace-nowrap">{new Date(t.created_at).toLocaleDateString('it-IT')}</span>
                </div>
                <p className="text-sm font-semibold text-white mb-1 truncate">{t.titolo}</p>
                <div className="flex items-center gap-3 text-xs text-muted-color">
                  <span>{t.tipo.replace(/_/g, ' ')}</span>
                  {t.reparto && <span>• {t.reparto}</span>}
                  {t.assegnato_a_nome && <span>• {t.assegnato_a_nome}</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Right Panel - Ticket Detail */}
      {selectedTicket && (
        <div className="w-1/2 flex flex-col bg-surface-elevated border border-border rounded-xl overflow-hidden">
          <div className="flex items-center justify-between p-4 border-b border-border">
            <div>
              <p className="text-xs font-mono text-muted-color">{selectedTicket.numero_ticket}</p>
              <h3 className="text-base font-bold text-white">{selectedTicket.titolo}</h3>
            </div>
            <button onClick={() => setSelectedTicket(null)} className="p-1.5 hover:bg-surface rounded-lg transition-colors">
              <Icon name="XMarkIcon" size={18} className="text-muted-color" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            <div className="flex flex-wrap gap-2">
              <span className={`text-xs px-2 py-1 rounded-full border ${prioritaColor[selectedTicket.priorita]}`}>{selectedTicket.priorita}</span>
              <span className={`text-xs px-2 py-1 rounded-full ${statoColor[selectedTicket.stato]}`}>{selectedTicket.stato.replace(/_/g, ' ')}</span>
              <span className="text-xs px-2 py-1 rounded-full bg-surface text-muted-color">{selectedTicket.tipo.replace(/_/g, ' ')}</span>
            </div>
            <p className="text-sm text-secondary-color">{selectedTicket.descrizione}</p>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><span className="text-muted-color">Reparto:</span> <span className="text-white">{selectedTicket.reparto || '-'}</span></div>
              <div><span className="text-muted-color">Aperto da:</span> <span className="text-white">{selectedTicket.aperto_da_nome || '-'}</span></div>
              <div><span className="text-muted-color">Assegnato a:</span> <span className="text-white">{selectedTicket.assegnato_a_nome || 'Non assegnato'}</span></div>
              <div><span className="text-muted-color">SLA:</span> <span className={slaColor[selectedTicket.sla_stato]}>{selectedTicket.sla_stato.replace(/_/g, ' ')}</span></div>
              {selectedTicket.ore_lavoro > 0 && <div><span className="text-muted-color">Ore lavoro:</span> <span className="text-white">{selectedTicket.ore_lavoro}h</span></div>}
              {selectedTicket.valutazione && <div><span className="text-muted-color">Valutazione:</span> <span className="text-amber-400">{'★'.repeat(selectedTicket.valutazione)}</span></div>}
            </div>
            {/* Cambio Stato */}
            <div>
              <p className="text-xs font-semibold text-muted-color mb-2">CAMBIA STATO</p>
              <div className="flex flex-wrap gap-1.5">
                {STATI.map(s => (
                  <button key={s} onClick={() => cambiaStato(selectedTicket.id, s)} className={`text-xs px-2 py-1 rounded-lg border transition-colors ${selectedTicket.stato === s ? 'border-sky-500 text-sky-400 bg-sky-500/10' : 'border-border text-muted-color hover:border-sky-500/50'}`}>
                    {s.replace(/_/g, ' ')}
                  </button>
                ))}
              </div>
            </div>
            {/* Commenti */}
            <div>
              <p className="text-xs font-semibold text-muted-color mb-2">COMMENTI ({commenti.length})</p>
              <div className="space-y-2 mb-3">
                {commenti.map(c => (
                  <div key={c.id} className={`rounded-lg p-3 text-sm ${c.visibile_utente ? 'bg-sky-500/10 border border-sky-500/20' : 'bg-surface border border-border'}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-semibold text-white text-xs">{c.autore_nome}</span>
                      <div className="flex items-center gap-2">
                        {c.visibile_utente && <span className="text-xs text-sky-400">Visibile utente</span>}
                        <span className="text-xs text-muted-color">{new Date(c.created_at).toLocaleString('it-IT')}</span>
                      </div>
                    </div>
                    <p className="text-secondary-color">{c.testo}</p>
                  </div>
                ))}
              </div>
              <div className="space-y-2">
                <textarea value={nuovoCommento} onChange={e => setNuovoCommento(e.target.value)} placeholder="Aggiungi commento..." rows={2} className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-sm text-white placeholder-muted-color resize-none" />
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 text-xs text-muted-color cursor-pointer">
                    <input type="checkbox" checked={commentoVisibile} onChange={e => setCommentoVisibile(e.target.checked)} className="rounded" />
                    Visibile all&apos;utente
                  </label>
                  <button onClick={aggiungiCommento} disabled={saving || !nuovoCommento.trim()} className="bg-sky-600 hover:bg-sky-500 disabled:opacity-50 text-white px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors">
                    {saving ? 'Invio...' : 'Invia'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* New Ticket Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-surface border border-border rounded-2xl w-full max-w-lg shadow-2xl">
            <div className="flex items-center justify-between p-5 border-b border-border">
              <h2 className="text-lg font-bold text-white">Nuovo Ticket</h2>
              <button onClick={() => setShowForm(false)} className="p-1.5 rounded-lg hover:bg-surface-elevated transition-colors">
                <Icon name="XMarkIcon" size={18} className="text-muted-color" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-secondary-color mb-1">Titolo *</label>
                <input value={form.titolo} onChange={e => setForm(p => ({ ...p, titolo: e.target.value }))} placeholder="Descrivi brevemente il problema" className="w-full bg-surface-elevated border border-border rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-sky-500" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-secondary-color mb-1">Descrizione *</label>
                <textarea value={form.descrizione} onChange={e => setForm(p => ({ ...p, descrizione: e.target.value }))} rows={3} placeholder="Descrizione dettagliata del problema" className="w-full bg-surface-elevated border border-border rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-sky-500 resize-none" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-1">Tipo</label>
                  <select value={form.tipo} onChange={e => setForm(p => ({ ...p, tipo: e.target.value }))} className="w-full bg-surface-elevated border border-border rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-sky-500">
                    {TIPI.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-1">Priorità</label>
                  <select value={form.priorita} onChange={e => setForm(p => ({ ...p, priorita: e.target.value }))} className="w-full bg-surface-elevated border border-border rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-sky-500">
                    {PRIORITA.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-1">Reparto</label>
                  <input value={form.reparto} onChange={e => setForm(p => ({ ...p, reparto: e.target.value }))} className="w-full bg-surface-elevated border border-border rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-sky-500" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-1">Aperto da</label>
                  <input value={form.aperto_da_nome} onChange={e => setForm(p => ({ ...p, aperto_da_nome: e.target.value }))} placeholder="Nome richiedente" className="w-full bg-surface-elevated border border-border rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-sky-500" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-secondary-color mb-1">Assegnato a (Tecnico)</label>
                <input value={form.assegnato_a_nome} onChange={e => setForm(p => ({ ...p, assegnato_a_nome: e.target.value }))} placeholder="Nome tecnico assegnatario" className="w-full bg-surface-elevated border border-border rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-sky-500" />
              </div>
            </div>
            <div className="flex gap-3 p-5 border-t border-border">
              <button onClick={() => setShowForm(false)} className="flex-1 py-2.5 rounded-xl border border-border text-sm font-semibold text-secondary-color hover:text-white hover:bg-surface-elevated transition-all">Annulla</button>
              <button onClick={creaTicket} disabled={saving} className="flex-1 py-2.5 rounded-xl bg-sky-600 hover:bg-sky-500 disabled:opacity-60 text-white text-sm font-bold transition-all">{saving ? 'Creazione...' : 'Crea Ticket'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
